const CONTROL_CHARS = /[\x00-\x1f]/;

/**
 * Sanitises a redirect target read from a URL query parameter (e.g. `?redirect=`)
 * or client-side form state before it's used for navigation (`window.location.href`,
 * a router redirect, ...). Without this, a link like
 * `/nl/inloggen?redirect=https://evil.example` logs a victim in on the real site and
 * then sends their browser straight to an attacker's page — an open redirect.
 *
 * Only a same-site path is accepted:
 *  - it must start with a single "/" (a relative path — this alone already rules out
 *    a scheme, since "https:" etc. can only be parsed as a scheme when there's no
 *    leading slash)
 *  - it must not start with "//" or "/\" — browsers treat both as a protocol-relative
 *    URL, so "//evil.example" navigates off-site exactly like "https://evil.example"
 *  - it must not contain "\n" or "\r" (header injection, and a way to smuggle a "//"
 *    or "/\" past the checks above via characters a URL parser strips later)
 *
 * Any other ASCII control character (e.g. a tab) is rejected too, for the same
 * reason as "\n"/"\r": browsers strip these from URLs before parsing, so
 * "/\t/evil.example" would otherwise slip through as "//evil.example" downstream.
 *
 * As a second layer, the value is also resolved against a fixed placeholder origin;
 * if the result doesn't stay on that origin, it's rejected. This catches any
 * URL-parsing quirk the checks above didn't anticipate (mirrors the approach in
 * `safe-href.ts`).
 *
 * Anything rejected falls back to `fallback`, which the caller controls and must
 * itself be a known-safe, same-site path.
 */
export function safeRedirectPath(
  raw: string | null | undefined,
  fallback: string
): string {
  if (!raw) return fallback;
  if (CONTROL_CHARS.test(raw)) return fallback;
  if (!raw.startsWith("/")) return fallback;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return fallback;

  try {
    const resolved = new URL(raw, "https://safe-redirect.invalid");
    if (resolved.origin !== "https://safe-redirect.invalid") return fallback;
  } catch {
    return fallback;
  }

  return raw;
}
